import {ref} from 'vue';
import {defineStore} from 'pinia';
import {ErrorInterface} from "~/types/interfaces/ErrorInterface";
import {useCSRF} from "~/stores/csrf";
import {useAPI} from "~/composables/useAPI";
import {PublicDocument, PublicDocumentFilters} from "~/types/interfaces/entitites/PublicDocumentInterface";
import {JudicialPrecedentFilters} from "~/types/interfaces/entitites/JudicialPrecedentInterface";

export const usePublicDocumentsClientStore = defineStore('publicDocumentsClient', () => {
    //https://pinia.vuejs.org/core-concepts/#setup-stores
    //ref()s become state properties
    const error = ref<ErrorInterface | null>(null);
    const publicDocument = ref<PublicDocument>({} as PublicDocument);
    const publicDocuments = ref<PublicDocument[]>([]);
    const useApi = useAPI();
    const csrfStore = useCSRF();
    const dataUpdatedAt = ref<Date>(new Date());
    const minutesToRefresh = ref<number>(30);

    // computed()s become getters
    const publicDocumentsList = computed(() => publicDocuments.value)
    const currentEntity = computed(() => publicDocument.value)

    //function()s become actions
    function setPublicDocument(publicDocumentData: PublicDocument) {
        publicDocument.value = publicDocumentData;
    }

    function setPublicDocuments(publicDocumentsData: [PublicDocument]) {
        publicDocuments.value = publicDocumentsData;
    }

    function setError(errorData: any) {
        error.value = errorData;
    }

    function resetErrors() {
        error.value = null;
    }

    async function list(forceRefresh: boolean = false) {
        if (!forceRefresh) {
            if (publicDocuments.value.length > 0 && useUtil().calculateElapsedMinutes(dataUpdatedAt.value, new Date()) <= minutesToRefresh.value) {
                return;
            }
        }

        await useApi.apiFetch('/client/public-document/list', {
            method: 'GET',
        }).then(({data}) => {
            try {
                setPublicDocuments(data);
            } catch (e) {
                console.log("exc", e);
            }

        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    async function listByFilter(data: PublicDocumentFilters) {
        await useApi.apiFetch('/client/public-document/list', {
            method: 'POST',
            body: JSON.stringify(data),
        }).then(({data}) => {
            try {
                setPublicDocuments(data);
            } catch (e) {
                console.log("exc", e);
            }

        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    async function view(hash: string) {
        await useApi.apiFetch(`/client/public-document/view/${hash}`, {
            method: 'GET',
        }).then(({data}) => {
            try {
                setPublicDocument(data);
            } catch (e) {
                console.log("exc", e);
            }
        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    async function download(hash: string) {
        return await useApi.apiFetch(`/client/public-document/download/${hash}`, {
            method: 'POST',
        });
    }

    return {
        error,
        publicDocumentsList,
        currentEntity,
        resetErrors,
        list,
        listByFilter,
        view,
        download
    };
});
